import {
  useState,
  useCallback,
  useEffect,
  type ReactNode,
  useContext,
} from "react";
import {
  SubscriptionsContext,
  type SubscriptionsContextType,
} from "./SubscriptionsContext";
import type { Subscription } from "../../../api/subscriptions";
import {
  callFetchSubscriptions,
  callCreateSubscription,
  callUpdateSubscription,
  callDeleteSubscription,
} from "../../../api/subscriptions";
import type { SubscriptionTier } from "../../../api/tiers";
import {
  callFetchTiers,
  callCreateTier,
  callUpdateTier,
  callDeleteTier,
} from "../../../api/tiers";
import { ModalContext } from "./ModalContext";

interface SubscriptionsContextProviderProps {
  children: ReactNode;
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error) return error.message;
  return fallback;
}

function axiosErrorMessage(error: unknown): string | null {
  const axiosMsg = (error as { response?: { data?: { message?: string } } })
    ?.response?.data?.message;
  return typeof axiosMsg === "string" ? axiosMsg : null;
}

export const SubscriptionsContextProvider = ({
  children,
}: SubscriptionsContextProviderProps) => {
  const { openModal } = useContext(ModalContext);

  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Loads subscriptions and tiers together from the server.
   * Used on login and after changes that affect both lists.
   */
  const reloadAll = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [subsResult, tiersResult] = await Promise.all([
        callFetchSubscriptions(),
        callFetchTiers(),
      ]);

      setSubscriptions(subsResult);
      setTiers(tiersResult);
    } catch (err) {
      console.error("Could not load subscriptions or tiers:", err);
      setError(errorMessage(err, "Could not load subscriptions or tiers"));
      setSubscriptions([]);
      setTiers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reloadAll();
  }, [reloadAll]);

  // Both names do the same thing — some screens call `refetch`, others `refetchTiers`.
  const refetch = reloadAll;
  const refetchTiers = reloadAll;

  const createSubscription = useCallback(
    async (data: { name: string; spots?: number; isCombo?: boolean }) => {
      try {
        setLoading(true);
        setError(null);
        const created = await callCreateSubscription(data);
        setSubscriptions((prev) => [...prev, created]);
        return created;
      } catch (err) {
        console.error("Error creating subscription:", err);
        setError(errorMessage(err, "Failed to create subscription"));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const updateSubscription = useCallback(
    async (
      id: string,
      data: {
        name?: string;
        spots?: number;
        isCombo?: boolean;
        isActive?: boolean;
      },
    ) => {
      try {
        setLoading(true);
        setError(null);
        const updated = await callUpdateSubscription(id, data);
        setSubscriptions((prev) =>
          prev.map((subscription) =>
            subscription.id === id ? updated : subscription,
          ),
        );
      } catch (err) {
        console.error("Error updating subscription:", err);
        setError(errorMessage(err, "Failed to update subscription"));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const deleteSubscription = useCallback(
    async (id: string) => {
      try {
        setLoading(true);
        setError(null);
        await callDeleteSubscription(id);
        setSubscriptions((prev) =>
          prev.filter((subscription) => subscription.id !== id),
        );
      } catch (err) {
        const message =
          axiosErrorMessage(err) ??
          errorMessage(err, "Failed to delete subscription");
        setError(message);
        openModal({
          body: message,
          showCloseButton: true,
        });
      } finally {
        setLoading(false);
      }
    },
    [openModal],
  );

  const createTier = useCallback(
    async (data: {
      subscriptionId: string;
      name: string;
      price: number;
      oldCsaMemberPrice: number;
      spots?: number;
      dependsOn?: string;
      takesFrom?: string[];
    }) => {
      try {
        setLoading(true);
        setError(null);
        const created = await callCreateTier(data);
        await reloadAll();
        return created;
      } catch (err) {
        console.error("Error creating tier:", err);
        setError(errorMessage(err, "Failed to create tier"));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [reloadAll],
  );

  const updateTier = useCallback(
    async (
      id: string,
      data: {
        name?: string;
        price?: number;
        oldCsaMemberPrice?: number;
        spots?: number;
        dependsOn?: string | null;
        takesFrom?: string[] | null;
      },
    ) => {
      try {
        setLoading(true);
        setError(null);
        await callUpdateTier(id, data);
        await reloadAll();
      } catch (err) {
        console.error("Error updating tier:", err);
        setError(errorMessage(err, "Failed to update tier"));
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [reloadAll],
  );

  const deleteTier = useCallback(
    async (id: string) => {
      try {
        setLoading(true);
        setError(null);
        await callDeleteTier(id);
        await reloadAll();
      } catch (err) {
        console.error("Error deleting tier:", err);
        const message =
          axiosErrorMessage(err) ?? errorMessage(err, "Failed to delete tier");
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [reloadAll],
  );

  const value: SubscriptionsContextType = {
    subscriptions,
    tiers,
    loading,
    error,
    createSubscription,
    updateSubscription,
    deleteSubscription,
    refetch,
    createTier,
    updateTier,
    refetchTiers,
    deleteTier,
  };

  return (
    <SubscriptionsContext.Provider value={value}>
      {children}
    </SubscriptionsContext.Provider>
  );
};
